/**
 * Walking an order book, exactly.
 *
 * Sizing a market order against depth is where float arithmetic hurt most: the
 * form priced only the depth that existed (10 BTC against 1 BTC of asks was
 * valued as 1 BTC and passed the buying-power check), and a `["0", "5"]` row
 * handed out 5 units for free because a zero price made the level cost nothing.
 *
 * Here every level is exact text, padding rows are skipped, and the part the
 * book cannot fill is reported as a `shortfall` instead of vanishing.
 *
 * Levels arrive as the API and the websocket send them, `[price, size]` of
 * strings, best-first (asks ascending, bids descending). `sizeForQuote` and
 * `quoteForSize` walk them in that order; `bestLevel` does not trust it. The
 * caller's array is never mutated — the book is SWR-owned.
 */
import { Dec, dec } from "./decimal";

/** `[price, size]`, possibly with an extra field (iv) the book carries along. */
export type BookLevel = ReadonlyArray<string | number>;
export type BookLevels = ReadonlyArray<BookLevel> | null | undefined;

/** Which column these levels are, so "best" means highest or lowest. */
export type BookSide = "bids" | "asks";

export interface BookQuote {
  price: Dec;
  size: Dec;
}

/**
 * Base sizes divide exactly onto 1e-18, the finest grid anything on-chain uses.
 * Always floored: a size that rounded up would spend more than the trader has.
 */
const SIZE_DECIMALS = 18;

/** A level with a real price AND real liquidity, or `null` for padding/garbage. */
const readLevel = (level: BookLevel | undefined): BookQuote | null => {
  if (!level) return null;
  const price = dec.parse(level[0] as Dec);
  const size = dec.parse(level[1] as Dec);
  if (price === null || size === null) return null;
  if (!dec.gt(price, "0") || !dec.gt(size, "0")) return null;
  return { price, size };
};

/** A positive amount as canonical text, or `null`. */
const positive = (value: Dec | number | null | undefined): Dec | null => {
  if (value === null || value === undefined) return null;
  const parsed = dec.parse(value);
  return parsed !== null && dec.gt(parsed, "0") ? parsed : null;
};

/**
 * The top of the book: the highest bid, or the lowest ask.
 *
 * Scans rather than reading index 0, so an unsorted snapshot cannot report a
 * mid-book level as the touch. Levels padded to `["0","0"]` and levels whose
 * size has been eaten to zero are not quotes and are skipped.
 */
export const bestLevel = (levels: BookLevels, side: BookSide): BookQuote | null => {
  let best: BookQuote | null = null;
  for (const raw of levels || []) {
    const level = readLevel(raw);
    if (!level) continue;
    if (
      !best ||
      (side === "bids"
        ? dec.gt(level.price, best.price)
        : dec.lt(level.price, best.price))
    ) {
      best = level;
    }
  }
  return best;
};

export interface SizeForQuoteResult {
  /** Base size the quote buys. */
  size: Dec;
  /** Quote the book's depth could not absorb — the insufficient-liquidity signal. */
  shortfall: Dec;
}

/**
 * How much of the asset `quote` buys walking `levels` best-first.
 *
 * Without a `fallbackPrice` the size is capped at the book's depth, exactly as
 * the trade form has always capped it: no other price is substituted for
 * missing depth (a stale limit price left in the form once sized a market order
 * larger than the amount typed). With one — a mark, say — the leftover is
 * converted at that price and added, for a preview that has to answer "what
 * does this money buy" even where the book is thin.
 *
 * `shortfall` always reports the quote the BOOK could not fill, whether or not
 * a fallback then priced it, so the liquidity warning stays truthful either way.
 */
export const sizeForQuote = (
  levels: BookLevels,
  quote: Dec,
  fallbackPrice?: Dec | null
): SizeForQuoteResult => {
  const budget = positive(quote);
  if (budget === null) return { size: "0", shortfall: "0" };

  let remaining = budget;
  let size = "0";
  for (const raw of levels || []) {
    if (!dec.gt(remaining, "0")) break;
    const level = readLevel(raw);
    if (!level) continue;
    const levelCost = dec.mul(level.price, level.size);
    if (dec.gte(remaining, levelCost)) {
      remaining = dec.sub(remaining, levelCost);
      size = dec.add(size, level.size);
    } else {
      size = dec.add(
        size,
        dec.div(remaining, level.price, SIZE_DECIMALS, "floor") ?? "0"
      );
      remaining = "0";
    }
  }

  const shortfall = dec.gt(remaining, "0") ? remaining : "0";
  const fallback = positive(fallbackPrice);
  if (fallback !== null && dec.gt(shortfall, "0")) {
    size = dec.add(
      size,
      dec.div(shortfall, fallback, SIZE_DECIMALS, "floor") ?? "0"
    );
  }
  return { size, shortfall };
};

export interface QuoteForSizeResult {
  /** What the size costs, INCLUDING the part the book cannot fill. */
  quote: Dec;
  /** The deepest price used — the execution bound this fill would need. */
  worstPrice: Dec;
  /** Base size the book's depth could not fill. */
  shortfall: Dec;
}

/**
 * What `size` costs against `levels`, including any part the book cannot fill.
 *
 * The shortfall is priced at the worst level the walk reached — the book's own
 * deepest quote — and only at `fallbackPrice` when the book offered nothing at
 * all. A shortfall that cannot be priced either way is reported but not charged
 * (the caller has the `shortfall` and can refuse), which is what the form does
 * today.
 */
export const quoteForSize = (
  levels: BookLevels,
  size: Dec,
  fallbackPrice?: Dec | null
): QuoteForSizeResult => {
  const wanted = positive(size);
  if (wanted === null) return { quote: "0", worstPrice: "0", shortfall: "0" };

  let remaining = wanted;
  let quote = "0";
  let worstPrice: Dec | null = null;
  for (const raw of levels || []) {
    if (!dec.gt(remaining, "0")) break;
    const level = readLevel(raw);
    if (!level) continue;
    const taken = dec.lt(remaining, level.size) ? remaining : level.size;
    quote = dec.add(quote, dec.mul(level.price, taken));
    remaining = dec.sub(remaining, taken);
    worstPrice = level.price;
  }

  const shortfall = dec.gt(remaining, "0") ? remaining : "0";
  const price = worstPrice ?? positive(fallbackPrice);
  if (price !== null && dec.gt(shortfall, "0")) {
    quote = dec.add(quote, dec.mul(price, shortfall));
  }
  return { quote, worstPrice: price ?? "0", shortfall };
};
